import React from 'react';
import { Body } from '@vds/typography';
import { TrailingTooltip } from '@vds/tooltips';
import styled from 'styled-components';
import { TextLink } from '@vds/buttons';
import { Icon } from '@vds/icons';
import { convertStringToElements } from '@vds/html-parsers';

import { isMobile, isWHWP2, getSession, viewport, getWHWSpoCookie } from '../../../../../common/Helpers';
import { onBtnClickClone } from './addonsCommon';
import { generateButtonQtyProps, generateSteepingInputProps, generateVdsButtonProps, generateWhwButtonProps } from './buttonData';
import { StyledWrapper } from '../../../../../common/styles';

const PricingWrapper = styled.div`
  min-height: 35px;
`;

const CapWrapper = styled.div`
  & + span {
    display: none;
  }
`;
const InlineWrapper = styled(StyledWrapper)`
  display: inline-block;
`;

export const generateVdsTileData = (
  title,
  newPrice,
  oldPrice,
  pricingText,
  cap,
  features,
  type,
  isStreamingDevice,
  footer,
  detailsLinkListener,
  discountAvailed,
  otherProps,
  isStreamingService,
  youtTubePromoBanner,
  youTubePricingTextLabel,
  setShowPromoDetailsModal,
  promoBannerDetailsModalHeader,
  promoBannerDetailsModalContent,
  isYoutubePremiumAdded,
  color,
) => {
  const detailsLinkElement = {
    textLink: {
      children: 'Review details',
      onClick: detailsLinkListener,
      ariaLabel: isWHWP2()?`review details | ${title}`:`review details about ${title}`,
    },
  };

  const tileHeader = otherProps?.useBrandLogoNamming ? (
    <div>
      <img width={otherProps.iconWidth || '106px'} src={otherProps.imageUrl} alt={title} />{' '}
    </div>
  ) : (
    title
  );
  const planType = getSession('FlowType');

  let pricingTextLabel = newPrice === '0' && {
    title: {
      children: pricingText,
    },
  };
  let capData = isWHWP2()
    ? cap && {
        children: cap.replace('XX.XX', discountAvailed).replace('$PlanName', planType === '5G' ? '5G Home Plus' : 'LTE Home Plus'),
        fillColor: color,
      }
    : cap && {
        children: cap.replace('XX.XX', discountAvailed).replace('$PlanName', planType === '5G' ? '5G Home Plus' : 'LTE Home Plus'),
      };

  if (isStreamingService && isYoutubePremiumAdded) {
    capData = {
      children: (
        <>
          <CapWrapper>
            <InlineWrapper>
              <Icon name="tag" size="large" color="#FFFFFF" />
            </InlineWrapper>
            <InlineWrapper styles="width:80%;margin-left: 10px;">
              {youtTubePromoBanner}
              <span>&nbsp;</span>
              <TextLink
                ariaLabel={`${youtTubePromoBanner} Details button`}
                onClick={() => setShowPromoDetailsModal(true)}
                tabIndex="0"
                type="standAlone"
                id="prmoDetails"
                size="small"
                surface="dark"
              >
                Details
              </TextLink>
            </InlineWrapper>
          </CapWrapper>
        </>
      ),
    };

    pricingTextLabel = {
      title: {
        primitive: 'p',
        children: (
          <TrailingTooltip
            typographySize="small"
            typographyPrimitive="p"
            typographyType="body"
            tooltipSize="small"
            tooltipTitle={
              <Body primitive="h2" size={isMobile() ? 'large' : 'small'} color="#000000" bold>
                {promoBannerDetailsModalHeader || ''}
              </Body>
            }
            tooltipContent={convertStringToElements(promoBannerDetailsModalContent || '')}
            bold={false}
            surface="light"
            typographyColor="#6F7171"
            ariaLabel={`More information on ${youTubePricingTextLabel}`}
          >
            {youTubePricingTextLabel}
          </TrailingTooltip>
        ),
      },
    };
  }

  if (isStreamingDevice && isYoutubePremiumAdded) {
    pricingTextLabel = {
      title: {
        children: (
          <PricingWrapper>
            <div />
          </PricingWrapper>
        ),
      },
    };
    capData = { children: '', fillColor: 'gray', surface: 'light' };
  }

  return {
    showBorder: true,
    backgroundColor: 'white',
    width: isMobile() ? '276px' : '280px',
    title: {
      children: tileHeader,
      primitive: 'h3',
    },
    oldPrice: oldPrice && {
      leadingText: {
        children: 'was',
      },
      price: oldPrice,
      term: 'month',
    },
    newPrice: newPrice !== '0' && {
      price: newPrice,
      term: isMonthlyBillable(type) ? 'month' : 'none',
      superscript: isYoutubePremiumAdded ? '1' : '',
    },
    pricingText: pricingTextLabel,
    cap: capData,
    details: {
      ...(detailsLinkListener ? detailsLinkElement : {}),
      list: features.map((feature) => ({ children: feature })),
      height: features?.length > 3 ? '260px' : '167px',
    },
    footer,
  };
};

export const prepareVDSTileProps = (props) => {
  const {
    title,
    newPrice,
    oldPrice,
    pricingText,
    cap,
    features = [],
    type,
    refData,
    bauTileData,
    whwTileData,
    addonProductData: { skuDetails } = {},
    detailsLinkListener,
    vzAddonCTAState,
    discountAvailed,
    youtTubePromoBanner,
    youTubePricingTextLabel,
    setShowPromoDetailsModal,
    isYoutubePremiumAdded,
    color,
    promoBannerDetailsModalHeader = '',
    promoBannerDetailsModalContent = '',
    ...otherProps
  } = props;
  const isStreamingDevice = type === 'streamingDevice';
  const isStreamingService = type === 'streamingService';
  const isWhwServices = type === 'whwServices';
  const tileDataList = bauTileData?.find((data) => data[skuDetails?.sorId] || data[skuDetails?.productId]) || {};
  const tileData = tileDataList[skuDetails?.sorId] || tileDataList[skuDetails?.productId];
  const whwTileDataList = whwTileData?.find((data) => data[skuDetails?.sorId] || data[skuDetails?.productId]) || {};
  const whwTilesData = whwTileDataList[skuDetails?.sorId] || whwTileDataList[skuDetails?.productId];
  const prevQuantity = tileData?.quantity || whwTilesData?.quantity;
  const updatedProps = { ...otherProps, tileData, whwTilesData };
  const quantityUpdateCB = (currentQuantity) => {
    const mockEvent = {
      currentTarget: { id: currentQuantity > prevQuantity ? '_Increment' : '_Minus' },
    };
    const buttonQuantityProps = generateButtonQtyProps(
      props.updateAddonQuantity,
      props.buttonSelectState,
      title,
      currentQuantity,
      refData,
      skuDetails,
      props.parentProps,
    );
    onBtnClickClone(mockEvent, buttonQuantityProps);
  };
  let ctaName = '';
  const SessionValueWHW = viewport() === 'mfapp' ? getWHWSpoCookie('whwSpoDetails') : getSession('whwSpoDetails');
  const parsedMVOSession = SessionValueWHW && JSON.parse(SessionValueWHW);
  const addedWWHRedemptionSPO = parsedMVOSession?.spoDetails.find((obj) => obj.current === true);
  const extOnProfile = parsedMVOSession?.extenderOnProfile;
  const downgradeDetails = getSession('downgradeDetails') && JSON.parse(getSession('downgradeDetails'))?.Downgrade;

  if (!isWHWP2()) {
    ctaName = isWhwServices
      ? whwTileDataList[skuDetails?.productId || skuDetails?.sorId]?.quantity
        ? 'Selected'
        : 'select'
      : tileDataList[skuDetails?.productId || skuDetails?.sorId]?.quantity
        ? 'Remove'
        : 'Add';
  }
  // if (parsedSession?.whwSpoDetails?.extenderOnProfile === '0'){
  //   console.log('shubham uncau',  ((whwTileDataList[skuDetails?.productId || skuDetails?.sorId]).sorId === addedRedemptionSPO.spoid ))
  // }
  if (isWHWP2()) {
    if (extOnProfile === '0' && !addedWWHRedemptionSPO?.spoDesc?.toLowerCase().includes('plus')) {
      ctaName =
        isWhwServices && whwTileDataList[skuDetails?.productId || skuDetails?.sorId]?.quantity
          ? 'Selected'
          : whwTileDataList[skuDetails?.productId || skuDetails?.sorId]?.sorId === addedWWHRedemptionSPO?.spoid
            ? 'Redeem'
            : 'Upgrade';   
    } else if (extOnProfile === '0' && addedWWHRedemptionSPO?.spoDesc.toLowerCase().includes('plus')) {
      ctaName =
        isWhwServices && whwTileDataList[skuDetails?.productId || skuDetails?.sorId]?.quantity
          ? 'Selected'
          // : whwTileDataList[skuDetails?.productId || skuDetails?.sorId]?.sorId === addedWWHRedemptionSPO?.spoid
             : 'Redeem'
            // : 'Downgrade';
    } else if (extOnProfile === '1') {
      ctaName =
        isWhwServices && whwTileDataList[skuDetails?.productId || skuDetails?.sorId]?.quantity
          ? 'Selected'
          : skuDetails?.categoryType.toLowerCase().includes('plus') && !prevQuantity
            ? 'Upgrade'
            : 'Select';
    } else if (extOnProfile === '2' && addedWWHRedemptionSPO?.spoDesc.toLowerCase().includes('plus')) {
      ctaName =
        isWhwServices && whwTileDataList[skuDetails?.productId || skuDetails?.sorId]?.quantity
          ? 'Selected'
          : whwTileDataList[skuDetails?.productId || skuDetails?.sorId]?.sorId === addedWWHRedemptionSPO?.spoid
            ? 'Redeem'
            : 'Downgrade';
    } else if (extOnProfile === '3') {
      ctaName = whwTileDataList[skuDetails?.productId || skuDetails?.sorId]?.quantity
        ? 'Selected'
        : downgradeDetails ? 'Select' : 'Downgrade';
    }
  }
  const ariaLabel = !isWHWP2() 
  ? `${tileDataList[skuDetails?.productId || skuDetails?.sorId]?.quantity ? 'Remove' : 'Add'} ${skuDetails?.displayName || ''} ${tileDataList[skuDetails?.productId || skuDetails?.sorId]?.quantity ? 'from your plan' : 'to your plan'}`
  : `${ctaName} | ${title}`;
  
  const steepingInput = generateSteepingInputProps(quantityUpdateCB, null, prevQuantity);
  const vdsButton = generateVdsButtonProps(prevQuantity ? props.removeAddon : props.addAddon, ctaName, ariaLabel, type, updatedProps);
  const whwButton = generateWhwButtonProps(prevQuantity ? props.removeAddon : props.addAddon, ctaName, ariaLabel, type, updatedProps);
  const footer =
    isStreamingDevice && prevQuantity
      ? steepingInput
      : isWhwServices
        ? extOnProfile === '1' && skuDetails?.categoryType.toLowerCase().includes('plus') && prevQuantity
          ? steepingInput
          : whwButton
        : vdsButton;
  return generateVdsTileData(
    title,
    newPrice,
    oldPrice,
    pricingText,
    cap,
    features,
    type,
    isStreamingDevice,
    footer,
    detailsLinkListener,
    discountAvailed,
    otherProps,
    isStreamingService,
    youtTubePromoBanner,
    youTubePricingTextLabel,
    setShowPromoDetailsModal,
    promoBannerDetailsModalHeader,
    promoBannerDetailsModalContent,
    isYoutubePremiumAdded,
    color,
  );
};

export const isMonthlyBillable = (type) =>
  type === 'streamingService' || type === 'vzProtectHome' || type === 'homePhoneService' || type === 'whwServices';
